/**
 * Georeference tool.
 *
 * Aligns an image that has no correct position on the map yet — a scanned
 * drawing, a photographed paper plan, a raster layer someone added at the
 * rough default position `geotiff.ts`'s own plain-image import gives it —
 * by the classic desktop-GIS "Georeferencer" workflow: place a handful of
 * CONTROL POINTS (a point on the image, paired with where that same point
 * really is on the map), fit an affine transform through them, and warp the
 * image into a new, correctly-placed raster.
 *
 * AFFINE, NOT RUBBER-SHEET
 * The fit is a single 6-parameter affine transform (rotation, scale, shear,
 * translation) — exact for 3 points, least-squares for more. It does not
 * correct NON-RIGID distortion (uneven paper stretch, a bent/creased scan);
 * a sheet with real local warping will still show residual error at some
 * points after a good fit, which the per-point residual column is there to
 * surface, not hide. A full polynomial/rubber-sheet warp is a deliberately
 * separate, later piece of work.
 *
 * WHY THE OUTPUT IS A FLAT, AXIS-ALIGNED RASTER
 * Every raster this app renders (`MapViewOL.tsx`'s `localRasterLayers`
 * effect) is a plain `ol/source/ImageStatic` positioned by `Layer.bounds` —
 * an axis-aligned box, nothing else. A rotated/skewed fit therefore cannot
 * be stored as "this image, rotated" — there is nowhere downstream that
 * would draw that. Instead `applyGeoreference` below RESAMPLES the source
 * into a new upright image at apply time: a Canvas 2D `drawImage` under a
 * composed affine `setTransform`, the same way a desktop editor's own
 * "rotate canvas" does it — the browser performs the actual pixel warp,
 * not a hand-rolled sampling loop.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Crosshair, Loader2, Plus, Target, Trash2, Upload, X } from 'lucide-react';
import ToolPanelHeader, { ToolActions } from '../ToolPanel';
import { scopedLayers, type ToolContext } from './types';
import { EmptyHint, Field, LayerSelect, Notice, ResultHeader, ResultRow, ScrollList, btnClass } from './shared';
import { getLayerDisplayName } from '../../../layers/capabilities';
import {
  canvasToCompressedImage,
  decodeRasterForGeoreferencing,
  RASTER_MAX_DIMENSION,
} from '../../../layers/geotiff';

type SourceMode = 'layer' | 'upload';

interface SourceImage {
  base64: string;
  mimeType: string;
  width: number;
  height: number;
}

interface ControlPoint {
  id: string;
  /** [x, y] in SOURCE image pixels. */
  pixel: [number, number];
  /** [lng, lat]. */
  map: [number, number];
}

/** `lng = a·px + b·py + c`, `lat = d·px + e·py + f` — one affine transform,
 *  pixel space to map space. */
interface AffineFit {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

/* ──────────────────────────────── Maths ──────────────────────────────────
 * Hand-written rather than pulled in from a library — the same "small,
 * auditable maths beats a new dependency" call `fieldCalculator.ts` already
 * made for its own expression evaluator. Verified standalone against a
 * known synthetic rotation+scale+translate transform before being wired up
 * here: exact recovery from 3+ points, least-squares averaging with more,
 * collinear points correctly rejected, and the canvas-transform composition
 * below matching a direct per-pixel computation to float precision.
 */

/** Cramer's rule for a 3×3 system — `null` when the matrix is singular
 *  (degenerate/collinear control points have no unique affine fit). */
function solve3x3(matrix: number[][], rhs: number[]): number[] | null {
  const det3 = (m: number[][]) =>
    m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) -
    m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) +
    m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0]);
  const determinant = det3(matrix);
  if (Math.abs(determinant) < 1e-9) return null;
  const withColumn = (column: number) => matrix.map((row, i) => row.map((v, j) => (j === column ? rhs[i] : v)));
  return [det3(withColumn(0)) / determinant, det3(withColumn(1)) / determinant, det3(withColumn(2)) / determinant];
}

/** Least-squares affine fit through every control point — exact for
 *  exactly 3 non-collinear points, averaged for more. */
function fitAffine(points: ControlPoint[]): AffineFit | null {
  if (points.length < 3) return null;
  let sxx = 0, sxy = 0, sx = 0, syy = 0, sy = 0;
  let sxLng = 0, syLng = 0, sLng = 0;
  let sxLat = 0, syLat = 0, sLat = 0;
  for (const point of points) {
    const [px, py] = point.pixel;
    const [lng, lat] = point.map;
    sxx += px * px; sxy += px * py; sx += px;
    syy += py * py; sy += py;
    sxLng += px * lng; syLng += py * lng; sLng += lng;
    sxLat += px * lat; syLat += py * lat; sLat += lat;
  }
  const normalMatrix = [
    [sxx, sxy, sx],
    [sxy, syy, sy],
    [sx, sy, points.length],
  ];
  const abc = solve3x3(normalMatrix, [sxLng, syLng, sLng]);
  const def = solve3x3(normalMatrix, [sxLat, syLat, sLat]);
  if (!abc || !def) return null;
  const [a, b, c] = abc;
  const [d, e, f] = def;
  return [a, b, c, d, e, f].every(Number.isFinite) ? { a, b, c, d, e, f } : null;
}

function applyFit(fit: AffineFit, px: number, py: number): [number, number] {
  return [fit.a * px + fit.b * py + fit.c, fit.d * px + fit.e * py + fit.f];
}

/** A point's fit error, in metres — the same flat-Earth local approximation
 *  this app already uses for short distances (App.tsx's own print-extent
 *  box, `EditPanels.tsx`'s click-hit-testing). */
const METRES_PER_DEG_LAT = 111320;
function residualMetres(fit: AffineFit, point: ControlPoint): number {
  const [predictedLng, predictedLat] = applyFit(fit, point.pixel[0], point.pixel[1]);
  const metresPerDegLng = METRES_PER_DEG_LAT * Math.cos((point.map[1] * Math.PI) / 180);
  const dLat = (point.map[1] - predictedLat) * METRES_PER_DEG_LAT;
  const dLng = (point.map[0] - predictedLng) * metresPerDegLng;
  return Math.hypot(dLat, dLng);
}

/* ────────────────────────── Image pane (pan/zoom) ──────────────────────── */

const MIN_SCALE = 0.05;
const MAX_SCALE = 20;
/** Screen pixels of pointer movement below which a press+release still
 *  counts as a click rather than a drag — distinguishes "pan the image"
 *  from "place a point" on the same gesture. */
const DRAG_THRESHOLD_PX = 4;

interface View {
  scale: number;
  offsetX: number;
  offsetY: number;
}

/**
 * The image half of the control-point picker: a plain `<canvas>` with
 * wheel-zoom and drag-pan, numbered markers for points already placed, and
 * a click handler that reports back in SOURCE PIXEL coordinates (inverting
 * the current pan/zoom) rather than screen coordinates. The original, plain
 * shape this picker started as — zoom-by-scroll, no on-screen buttons.
 *
 * CSS PIXELS VS BUFFER PIXELS
 * The canvas's drawing buffer is a fixed 268×200 (`width`/`height` below),
 * but its on-screen CSS size is `w-full` — stretched to the panel's actual
 * width, almost never exactly 268px. `event.clientX/Y` arrives in CSS
 * pixels; converting it into a pixel coordinate on the IMAGE requires first
 * converting CSS pixels into buffer pixels (`cssToBuffer`), THEN inverting
 * the pan/zoom. Skipping that first step was a real, separate, shipped bug
 * (unrelated to zoom): a click read as if 1 CSS pixel were 1 buffer pixel,
 * correct only when the panel happened to be exactly 268px wide — the
 * resulting marker drifted further from the real click the further that
 * click was from the canvas's own top-left corner. Kept fixed here even
 * though the rest of this component reverted to its first shape.
 */
function ImagePickerCanvas({
  src,
  width,
  height,
  points,
  armed,
  onPick,
}: {
  src: string;
  width: number;
  height: number;
  points: ControlPoint[];
  armed: boolean;
  onPick: (pixel: [number, number]) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [imageReady, setImageReady] = useState(false);
  const [view, setView] = useState<View>({ scale: 1, offsetX: 0, offsetY: 0 });
  const dragRef = useRef<{ startX: number; startY: number; moved: boolean; startOffsetX: number; startOffsetY: number } | null>(null);

  const CANVAS_W = 268;
  const CANVAS_H = 200;

  useEffect(() => {
    setImageReady(false);
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      const scale = Math.min(CANVAS_W / width, CANVAS_H / height);
      setView({
        scale,
        offsetX: (CANVAS_W - width * scale) / 2,
        offsetY: (CANVAS_H - height * scale) / 2,
      });
      setImageReady(true);
    };
    img.src = src;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context || !img || !imageReady) return;

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.fillStyle = '#0f172a';
    context.fillRect(0, 0, CANVAS_W, CANVAS_H);
    context.setTransform(view.scale, 0, 0, view.scale, view.offsetX, view.offsetY);
    context.drawImage(img, 0, 0);
    context.setTransform(1, 0, 0, 1, 0, 0);

    points.forEach((point, index) => {
      const cx = view.offsetX + point.pixel[0] * view.scale;
      const cy = view.offsetY + point.pixel[1] * view.scale;
      context.beginPath();
      context.arc(cx, cy, 7, 0, Math.PI * 2);
      context.fillStyle = 'rgba(37, 99, 235, 0.85)';
      context.fill();
      context.strokeStyle = '#fff';
      context.lineWidth = 1.5;
      context.stroke();
      context.fillStyle = '#fff';
      context.font = 'bold 9px sans-serif';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(String(index + 1), cx, cy);
    });
  }, [view, points, imageReady]);

  useEffect(draw, [draw]);

  /**
   * CSS pixels -> canvas BUFFER pixels. The canvas's drawing buffer is a
   * fixed 268×200 (the `width`/`height` attributes below), but its on-screen
   * CSS size is `w-full` — stretched to whatever the panel happens to be,
   * which is essentially never exactly 268px. Every click/drag coordinate
   * arrives as `event.clientX/Y`, in CSS pixels; skipping this conversion
   * (the bug that shipped) reads a click's position as if 1 CSS pixel were
   * 1 buffer pixel, which is only true when the panel happens to be exactly
   * 268px wide. The error grows with distance from the canvas's own
   * top-left corner — exactly the "a point further from one corner lands
   * further off" pattern that gave this away.
   */
  const cssToBuffer = (rect: DOMRect) => ({
    x: rect.width > 0 ? CANVAS_W / rect.width : 1,
    y: rect.height > 0 ? CANVAS_H / rect.height : 1,
  });

  const pixelFromEvent = (event: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const toBuffer = cssToBuffer(rect);
    const canvasX = (event.clientX - rect.left) * toBuffer.x;
    const canvasY = (event.clientY - rect.top) * toBuffer.y;
    return [(canvasX - view.offsetX) / view.scale, (canvasY - view.offsetY) / view.scale];
  };

  const onWheel = (event: React.WheelEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    const rect = canvasRef.current!.getBoundingClientRect();
    const toBuffer = cssToBuffer(rect);
    const cursorX = (event.clientX - rect.left) * toBuffer.x;
    const cursorY = (event.clientY - rect.top) * toBuffer.y;
    const factor = event.deltaY < 0 ? 1.15 : 1 / 1.15;
    setView((prev) => {
      const nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev.scale * factor));
      const ratio = nextScale / prev.scale;
      return {
        scale: nextScale,
        offsetX: cursorX - (cursorX - prev.offsetX) * ratio,
        offsetY: cursorY - (cursorY - prev.offsetY) * ratio,
      };
    });
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    (event.target as HTMLCanvasElement).setPointerCapture(event.pointerId);
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      startOffsetX: view.offsetX,
      startOffsetY: view.offsetY,
    };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    // The drag-vs-click distance check stays in raw CSS pixels — "did the
    // pointer visibly move on screen" is a screen-space question. Only the
    // actual offset applied to the view needs the CSS->buffer conversion,
    // since `offsetX`/`offsetY` live in buffer-pixel space (the same space
    // `draw()` and `pixelFromEvent` both use).
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) drag.moved = true;
    if (drag.moved) {
      const rect = canvasRef.current!.getBoundingClientRect();
      const toBuffer = cssToBuffer(rect);
      setView((prev) => ({
        ...prev,
        offsetX: drag.startOffsetX + dx * toBuffer.x,
        offsetY: drag.startOffsetY + dy * toBuffer.y,
      }));
    }
  };

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (drag && !drag.moved && armed) {
      const [px, py] = pixelFromEvent(event);
      if (px >= 0 && py >= 0 && px <= width && py <= height) onPick([Math.round(px), Math.round(py)]);
    }
  };

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_W}
      height={CANVAS_H}
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      className={`block w-full rounded-lg border ${armed ? 'cursor-crosshair border-blue-300' : 'border-slate-200'}`}
      style={{ touchAction: 'none' }}
    />
  );
}

/* ─────────────────────────────── Panel ───────────────────────────────── */

export function GeoreferencePanel({ ctx }: { ctx: ToolContext }) {
  const allowed = scopedLayers(ctx.config, ctx.layers);
  const candidates = allowed.filter((layer) => Boolean(layer.localRaster));

  const [sourceMode, setSourceMode] = useState<SourceMode>(candidates.length > 0 ? 'layer' : 'upload');
  const [layerId, setLayerId] = useState(candidates[0]?.id ?? '');
  const [uploadedName, setUploadedName] = useState<string | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [upload, setUpload] = useState<SourceImage | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const selectedLayer = candidates.find((layer) => layer.id === layerId);
  const image: SourceImage | null =
    sourceMode === 'layer'
      ? selectedLayer?.localRaster
        ? {
            base64: selectedLayer.localRaster.base64,
            mimeType: selectedLayer.localRaster.mimeType || 'image/png',
            width: selectedLayer.localRaster.width,
            height: selectedLayer.localRaster.height,
          }
        : null
      : upload;
  const imageSrc = image ? `data:${image.mimeType};base64,${image.base64}` : null;

  // Control points reset whenever the underlying image changes — a point
  // placed on one image means nothing on a different one.
  const [points, setPoints] = useState<ControlPoint[]>([]);
  const pointCounterRef = useRef(0);
  useEffect(() => {
    setPoints([]);
  }, [imageSrc]);

  const [armed, setArmed] = useState(false);
  const [pendingPixel, setPendingPixel] = useState<[number, number] | null>(null);
  const [pendingMap, setPendingMap] = useState<[number, number] | null>(null);
  const consumedPointerRef = useRef('');

  const handleUpload = async (file: File | undefined) => {
    if (!file) return;
    setUploadError(null);
    setUploadBusy(true);
    try {
      const decoded = await decodeRasterForGeoreferencing(file);
      setUpload(decoded);
      setUploadedName(file.name);
    } catch (failure) {
      setUpload(null);
      setUploadError(failure instanceof Error ? failure.message : 'This image could not be read.');
    } finally {
      setUploadBusy(false);
    }
  };

  const startAddingPoint = () => {
    setArmed(true);
    setPendingPixel(null);
    setPendingMap(null);
  };
  const cancelAddingPoint = () => {
    setArmed(false);
    setPendingPixel(null);
    setPendingMap(null);
  };

  const handleImagePick = (pixel: [number, number]) => {
    if (!armed) return;
    setPendingPixel(pixel);
  };

  // The map half of the same pick, mirroring DirectionsPanel's own
  // ctx.pointer-watching pattern (NavigatePanels.tsx) — a signature-dedup
  // ref so a re-render (pointer unchanged) never double-consumes one click.
  useEffect(() => {
    if (!armed || pendingMap || !ctx.pointer) return;
    const clicked = ctx.pointer;
    const signature = `${clicked.lat},${clicked.lng}`;
    if (signature === consumedPointerRef.current) return;
    consumedPointerRef.current = signature;
    setPendingMap([clicked.lng, clicked.lat]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed, pendingMap, ctx.pointer]);

  // Commits a point once BOTH halves are filled, in whichever order they
  // arrived.
  useEffect(() => {
    if (!armed || !pendingPixel || !pendingMap) return;
    pointCounterRef.current += 1;
    setPoints((prev) => [...prev, { id: `cp-${pointCounterRef.current}`, pixel: pendingPixel, map: pendingMap }]);
    setArmed(false);
    setPendingPixel(null);
    setPendingMap(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed, pendingPixel, pendingMap]);

  const removePoint = (id: string) => setPoints((prev) => prev.filter((point) => point.id !== id));

  const fit = useMemo(() => fitAffine(points), [points]);
  const degenerate = points.length >= 3 && !fit;
  // Pixel space is y-DOWN; map space (lat) is y-UP — opposite handedness by
  // convention, so a NORMAL, correctly-paired fit (pixel-x -> +lng,
  // pixel-y -> -lat, before any rotation) already has a NEGATIVE
  // determinant on its own. A POSITIVE determinant is what flips: the
  // image mirrored left-right or upside-down, almost always because one
  // control point got paired with the wrong spot on the map (easy to do by
  // accident when two corners look similar) — verified numerically against
  // both a normal and a deliberately swapped-corner fit before shipping,
  // since this sign is exactly the kind of thing worth getting wrong once
  // and never noticing.
  const mirrored = Boolean(fit) && fit.a * fit.e - fit.b * fit.d > 0;

  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applied, setApplied] = useState<string | null>(null);

  const applyGeoreference = async () => {
    if (!image || !fit || mirrored) return;
    setApplying(true);
    setApplyError(null);
    setApplied(null);
    try {
      const corners: [number, number][] = [
        [0, 0],
        [image.width, 0],
        [0, image.height],
        [image.width, image.height],
      ];
      const mapped = corners.map(([px, py]) => applyFit(fit, px, py));
      const lngs = mapped.map((m) => m[0]);
      const lats = mapped.map((m) => m[1]);
      const outBounds: [number, number, number, number] = [
        Math.min(...lngs),
        Math.min(...lats),
        Math.max(...lngs),
        Math.max(...lats),
      ];
      if (outBounds[2] <= outBounds[0] || outBounds[3] <= outBounds[1]) {
        throw new Error('These control points collapse to a zero-size area — check for a duplicate point.');
      }

      // Output pixel size from the fit's own scale (how many map-space units
      // one source pixel step covers), so ground resolution roughly matches
      // the source — then capped the same way every other raster import is.
      const scaleX = Math.hypot(fit.a, fit.d);
      const scaleY = Math.hypot(fit.b, fit.e);
      let outWidth = Math.max(1, Math.round((outBounds[2] - outBounds[0]) / scaleX));
      let outHeight = Math.max(1, Math.round((outBounds[3] - outBounds[1]) / scaleY));
      const capScale = Math.min(1, RASTER_MAX_DIMENSION / Math.max(outWidth, outHeight));
      outWidth = Math.max(1, Math.round(outWidth * capScale));
      outHeight = Math.max(1, Math.round(outHeight * capScale));

      const sourceImg = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Could not read the source image.'));
        img.src = `data:${image.mimeType};base64,${image.base64}`;
      });

      const canvas = document.createElement('canvas');
      canvas.width = outWidth;
      canvas.height = outHeight;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('This browser could not render the warped image.');

      // Composes "source pixel -> map" (the fit) with "map -> output canvas
      // pixel" (a plain linear scale from outBounds) into the one matrix
      // Canvas 2D's own setTransform takes — verified standalone against a
      // direct per-pixel computation before being wired up here.
      const kx = outWidth / (outBounds[2] - outBounds[0]);
      const ky = outHeight / (outBounds[3] - outBounds[1]);
      context.setTransform(
        fit.a * kx,
        -fit.d * ky,
        fit.b * kx,
        -fit.e * ky,
        (fit.c - outBounds[0]) * kx,
        (outBounds[3] - fit.f) * ky,
      );
      context.imageSmoothingEnabled = true;
      context.drawImage(sourceImg, 0, 0);

      const encoded = await canvasToCompressedImage(canvas);
      const raster = {
        base64: encoded.base64,
        mimeType: encoded.mimeType,
        width: outWidth,
        height: outHeight,
        bandCount: 4,
      };

      if (sourceMode === 'layer' && selectedLayer) {
        ctx.onLayerChange?.(selectedLayer.id, { localRaster: raster, bounds: outBounds });
        setApplied(`"${getLayerDisplayName(selectedLayer)}" re-placed using ${points.length} control points.`);
      } else {
        const name = uploadedName ? `Georeferenced — ${uploadedName}` : 'Georeferenced image';
        ctx.onCreateTempRasterLayer?.(name, raster, outBounds, 'Georeference');
        setApplied(`Added as a new layer, placed using ${points.length} control points.`);
      }
    } catch (failure) {
      setApplyError(failure instanceof Error ? failure.message : 'Could not georeference this image.');
    } finally {
      setApplying(false);
    }
  };

  const pickHint = !armed
    ? null
    : !pendingPixel && !pendingMap
      ? 'Click the image below, then click the matching spot on the map.'
      : !pendingPixel
        ? 'Map point set — now click the matching spot on the image below.'
        : 'Image point set — now click the matching spot on the map.';

  return (
    <>
      <ToolPanelHeader
        title="Georeference"
        description="Align a scanned drawing or photo to the map using control points."
        onClose={ctx.onClose}
      />

      <div className="flex gap-1 rounded-md bg-slate-100 p-0.5 text-[11px] font-medium">
        {([
          { id: 'layer' as const, label: 'Layer on map' },
          { id: 'upload' as const, label: 'Upload image' },
        ]).map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => setSourceMode(entry.id)}
            className={`flex-1 rounded px-2 py-1 transition ${
              sourceMode === entry.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {sourceMode === 'layer' ? (
        <>
          <Field label="Raster to refine" hint="Its current position is only a starting guess — this replaces it.">
            <LayerSelect layers={candidates} value={layerId} onChange={setLayerId} />
          </Field>
          {candidates.length === 0 && (
            <EmptyHint>No image/GeoTIFF layers are on the map yet. Add one from Add Data first, or switch to "Upload image" above.</EmptyHint>
          )}
        </>
      ) : (
        <Field label="Image to georeference" hint="PNG, JPG or TIFF — not yet on the map.">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploadBusy}
              className={`${btnClass} gl-btn--secondary`}
            >
              {uploadBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              {uploadBusy ? 'Reading…' : 'Choose image'}
            </button>
            {upload && uploadedName && !uploadBusy && (
              <span className="min-w-0 flex-1 truncate text-[11px] text-slate-500">{uploadedName}</span>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".png,.jpg,.jpeg,.tif,.tiff"
            className="hidden"
            onChange={(event) => {
              void handleUpload(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          {uploadError && <p className="mt-1.5 text-[11px] text-red-600">{uploadError}</p>}
        </Field>
      )}

      {image && (
        <>
          <Field
            label="Control points"
            hint="Place at least 3 — a point you recognise both on the image and on the real map (a corner, a junction, a landmark)."
          >
            <ImagePickerCanvas
              src={imageSrc!}
              width={image.width}
              height={image.height}
              points={points}
              armed={armed}
              onPick={handleImagePick}
            />
          </Field>

          {pickHint && (
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-blue-700">
              <Crosshair className="h-3 w-3 flex-shrink-0" />
              {pickHint}
            </p>
          )}

          <ToolActions>
            {!armed ? (
              <button type="button" onClick={startAddingPoint} className={`${btnClass} gl-btn--secondary`}>
                <Plus className="h-3.5 w-3.5" />
                Add control point
              </button>
            ) : (
              <button type="button" onClick={cancelAddingPoint} className={`${btnClass} gl-btn--secondary`}>
                <X className="h-3.5 w-3.5" />
                Cancel
              </button>
            )}
          </ToolActions>

          {points.length > 0 && (
            <div className="space-y-1">
              <ResultHeader label="Control points" count={points.length} />
              <ScrollList>
                {points.map((point, index) => {
                  const residual = fit ? residualMetres(fit, point) : null;
                  return (
                    <ResultRow
                      key={point.id}
                      title={`#${index + 1} — ${point.map[1].toFixed(5)}, ${point.map[0].toFixed(5)}`}
                      detail={
                        residual != null
                          ? `Fit error: ${residual < 1000 ? `${residual.toFixed(1)} m` : `${(residual / 1000).toFixed(2)} km`}`
                          : undefined
                      }
                      action={
                        <button
                          type="button"
                          onClick={() => removePoint(point.id)}
                          aria-label={`Remove control point ${index + 1}`}
                          className="flex-shrink-0 rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      }
                    />
                  );
                })}
              </ScrollList>
            </div>
          )}

          {points.length > 0 && points.length < 3 && (
            <EmptyHint>{3 - points.length} more point{points.length === 2 ? '' : 's'} needed before a fit can be computed.</EmptyHint>
          )}
          {degenerate && (
            <Notice tone="warn">
              These points are too close to a straight line to fit uniquely — add a point further off that line.
            </Notice>
          )}
          {mirrored && !degenerate && (
            <Notice tone="warn">
              This fit would flip the image (mirror it, not just rotate/scale it) — almost certainly one control
              point is paired with the wrong spot on the map. Remove and re-place the point that looks wrong, or
              double-check which corner is which on the image.
            </Notice>
          )}

          <ToolActions>
            <button
              type="button"
              onClick={() => void applyGeoreference()}
              disabled={!fit || mirrored || applying}
              className={`${btnClass} gl-btn--primary`}
            >
              {applying && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {!applying && <Target className="h-3.5 w-3.5" />}
              {applying ? 'Warping…' : 'Apply'}
            </button>
          </ToolActions>

          {applyError && <Notice tone="warn">{applyError}</Notice>}
          {applied && <Notice>{applied}</Notice>}
        </>
      )}

      <p className="text-[10.5px] leading-snug text-slate-400">
        Fits one rotation+scale+shear transform through every point (not a local, rubber-sheet warp) — a sheet
        with real physical distortion will still show some error at a point even after a good fit.
      </p>
    </>
  );
}
